import React, { useEffect, Suspense } from "react"
import { Route, BrowserRouter, Routes, Navigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { selectToken, login } from "./redux/store/auth/authSlice"
import CommonLayout from "./components/initialComponents/CommonLayout"
import LoginScreen from "./pages/Auth/login/LoginScreen"
import NotFound from "./pages/Auth/NotFound"
import FindUser from "./pages/User/findUser/FindUser"
import CreateUser from "./pages/User/createUser/CreateUser"
import BannedUsers from "./pages/User/bannedUsers/BannedUsers"
import WaitingApprovalUser from "./pages/User/waitingApprovalUser/WaitingApprovalUser"
import DoApprovalScreen from "./pages/User/doApprovalUsers/DoApprovalScreen"
import GetAllUsers from "./pages/User/getAllUsers/GetAllUsers"
import UserTaggedPostsScreen from "./pages/User/userTaggedPosts/UserTaggedPostsScreen"
import FindBrand from "./pages/Brand/findBrand/FindBrand"
import AllBrands from "./pages/Brand/allBrands/AllBrands"
import FindCampaign from "./pages/Campaign/findCampaign/FindCampaign"
import AllCampaign from "./pages/Campaign/allCampaigns/AllCampaign"
import CampaignStats from "./pages/Campaign/campaignStats"
import Dashboard from "./pages/Dashboard/dashboard/Dashboard"
import HashtagSearch from "./pages/Dashboard/hashtagSearch/HashtagSearch"
import PopularBrands from "./pages/Dashboard/popularBrands/PopularBrands"
import WpMessages from "./pages/Dashboard/wpMessages/WpMessages"
import AllApplications from "./pages/Application/allApplications/AllApplications"
import APIdocsScreen from "./pages/ApiDoc/APIdocsScreen"
import ContentShared from "./pages/Application/contentShared/ContentShared"
import UserPayments from "./pages/Dashboard/userPayments/UserPayments"
import SmlManager from "./pages/Dashboard/smlManager/SmlManager"
import AgencyApplications from "./pages/Application/agencyApplications/AgencyApplications"
import ApplicationDone from "./pages/Application/applicationDone/ApplicationDone"
import ApplicationStats from "./pages/Application/applicationStats/ApplicationStats"
import LiveApplicationStats from "./pages/Application/liveApplicationStats/LiveApplicationStats"
import AutomatedBlog from "./pages/Blog/automatedBlog/AutomatedBlog"
import BrandStateStats from "./pages/Brand/brandStateStats/BrandStateStats"
import UnregisteredBrands from "./pages/Brand/unregisteredBrands/UnregisteredBrands"
import NewRegistrations from "./pages/Brand/newRegistrations/NewRegistrations"
import CustomerSuccess from "./pages/Brand/customerSuccess/CustomerSuccess"
import BotAccountScanning from "./pages/User/botAccountScanning/BotAccountScanning"
import UsersByCategories from "./pages/User/usersByCategories/UsersByCategories"
import Runpod from "./pages/Dashboard/runpod/Runpod"
import WhatsAppButtonManager from "./pages/Dashboard/whatsappButton/WhatsAppButtonManager"
import HashtagPipeline from "./pages/Dashboard/hashtagPipeline/HashtagPipeline"
import ExternalUserPipeline from "./pages/Dashboard/externalUserPipeline/ExternalUserPipeline"

function App() {
  const token = useSelector(selectToken)
  const dispatch = useDispatch()

  useEffect(() => {
    const tokenString = localStorage.getItem("token")
    const userString = localStorage.getItem("user")
    let user = null
    try {
      user = userString ? JSON.parse(userString) : null
    } catch {
      user = null
    }
    if (tokenString && user && !token) {
      dispatch(login({ token: tokenString, user }))
    }
  }, [dispatch, token])

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={token ? <Navigate to="/dashboard/statistics" /> : <Navigate to="/auth/login" />}
        />
        <Route
          path="/auth/*"
          element={token ? <Navigate to="/dashboard/statistics" /> : <AuthLayout />}
        />
        <Route path="/dashboard/*" element={token ? <DashboardLayout /> : <AuthLayout />} />
        <Route path="/user/*" element={token ? <UserLayout /> : <AuthLayout />} />
        <Route path="/brands/*" element={token ? <BrandsLayout /> : <AuthLayout />} />
        <Route path="/campaigns/*" element={token ? <CampaignsLayout /> : <AuthLayout />} />
        <Route path="/applications/*" element={token ? <ApplicationsLayout /> : <AuthLayout />} />
        <Route path="/blog/*" element={token ? <BlogLayout /> : <AuthLayout />} />
        <Route path="/api-docs/*" element={token ? <DocsLayout /> : <AuthLayout />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App

function AuthLayout() {
  return (
    <Routes>
      <Route path="/login" element={<LoginScreen />} />
    </Routes>
  )
}

function DashboardLayout() {
  return (
    <>
      <Suspense fallback={<div>Loading...</div>}>
        <CommonLayout>
          <Routes>
            <Route path="/statistics" element={<Dashboard />} />
            <Route path="/hashtag-search" element={<HashtagSearch />} />
            <Route path="/popular-brands" element={<PopularBrands />} />
            <Route path="/wp-messages" element={<WpMessages />} />
            <Route path="/user-payments" element={<UserPayments />} />
            <Route path="/sml-manager" element={<SmlManager />} />
            <Route path="/runpod" element={<Runpod />} />
            <Route path="/whatsapp-button" element={<WhatsAppButtonManager />} />
            <Route path="/hashtag-pipeline" element={<HashtagPipeline />} />
            <Route path="/external-user-pipeline" element={<ExternalUserPipeline />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </CommonLayout>
      </Suspense>
    </>
  )
}

function UserLayout() {
  return (
    <>
      <Suspense fallback={<div>Loading...</div>}>
        <CommonLayout>
          <Routes>
            <Route path="/find" element={<FindUser />} />
            <Route path="/create" element={<CreateUser />} />
            <Route path="/find-waiting-approval" element={<WaitingApprovalUser />} />
            <Route path="/do-approval" element={<DoApprovalScreen />} />
            <Route path="/get-all" element={<GetAllUsers />} />
            <Route path="/find/:id" element={<FindUser />} />
            <Route path="/user-tagged-posts" element={<UserTaggedPostsScreen />} />
            <Route path="/banned-users" element={<BannedUsers />} />
            <Route path="/bot-account-scanning" element={<BotAccountScanning />} />
            <Route path="/users-by-categories" element={<UsersByCategories />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </CommonLayout>
      </Suspense>
    </>
  )
}

function BrandsLayout() {
  return (
    <>
      <Suspense fallback={<div>Loading...</div>}>
        <CommonLayout>
          <Routes>
            <Route path="/find" element={<FindBrand />} />
            <Route path="/get-all" element={<AllBrands />} />
            <Route path="/find/:id" element={<FindBrand />} />
            <Route path="/state-stats" element={<BrandStateStats />} />
            <Route path="/unregistered" element={<UnregisteredBrands />} />
            <Route path="/new-registrations" element={<NewRegistrations />} />
            <Route path="/customer-success" element={<CustomerSuccess />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </CommonLayout>
      </Suspense>
    </>
  )
}

function CampaignsLayout() {
  return (
    <>
      <Suspense fallback={<div>Loading...</div>}>
        <CommonLayout>
          <Routes>
            <Route path="/find/:_id" element={<FindCampaign />} />
            <Route path="/get-all" element={<AllCampaign />} />
            <Route path="/statistics" element={<CampaignStats />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </CommonLayout>
      </Suspense>
    </>
  )
}

function ApplicationsLayout() {
  return (
    <>
      <Suspense fallback={<div>Loading...</div>}>
        <CommonLayout>
          <Routes>
            <Route path="/all-applications" element={<AllApplications />} />
            <Route path="/content-shared" element={<ContentShared />} />
            <Route path="/agency-applications" element={<AgencyApplications />} />
            <Route path="/application-done" element={<ApplicationDone />} />
            <Route path="/application-stats" element={<ApplicationStats />} />
            <Route path="/live-application-stats" element={<LiveApplicationStats />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </CommonLayout>
      </Suspense>
    </>
  )
}

function BlogLayout() {
  return (
    <>
      <Suspense fallback={<div>Loading...</div>}>
        <CommonLayout>
          <Routes>
            <Route path="/automated-blog" element={<AutomatedBlog />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </CommonLayout>
      </Suspense>
    </>
  )
}

function DocsLayout() {
  return (
    <>
      <Suspense fallback={<div>Loading...</div>}>
        <CommonLayout>
          <Routes>
            <Route path="/document" element={<APIdocsScreen />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </CommonLayout>
      </Suspense>
    </>
  )
}
